import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useFormData } from '../data/useFormData';
import { useCallback, useEffect, useState } from 'react';
import { usePortalData } from '../data/usePortalData';
import { View } from 'foundations-components/components/View';
import { useTicketCreation } from '../components/TicketCreationProvider';
import styled from 'foundations-theming-specialty/utils';
import { useLoginVerify } from '../data/useLoginVerify';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VersionOneForm = ({
  formGuid,
  portalId,
  env,
  region,
  onFormLoaded,
  onFormSubmitted
}) => {
  const renderForm = useCallback(() => {
    // @ts-expect-error Untyped global
    window.hbspt.forms.create({
      portalId,
      formId: formGuid,
      region,
      env,
      target: `#form-container`
    });
  }, [formGuid, portalId, env, region]);
  useEffect(() => {
    const formsDomain = env === 'qa' ? 'hsformsqa.net' : 'hsforms.net';
    const script = document.createElement('script');
    script.src = `//js.${formsDomain}/forms/embed/v2.js`;
    script.type = 'text/javascript';
    script.onload = renderForm;
    document.body.appendChild(script);
    const handleMessage = event => {
      if (typeof event === 'object' && event !== null && 'data' in event) {
        const {
          type,
          id,
          eventName
        } = event.data;
        if (type !== 'hsFormCallback' || id !== formGuid) {
          return;
        }
        if (eventName === 'onFormReady') {
          onFormLoaded();
        }
        if (eventName === 'onFormSubmitted') {
          onFormSubmitted();
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      document.body.removeChild(script);
      window.removeEventListener('message', handleMessage);
    };
  }, [renderForm, onFormLoaded, onFormSubmitted, formGuid, env]);
  return /*#__PURE__*/_jsx(View, {
    inlineSize: "100%",
    id: "form-container",
    padding: "600"
  });
};
const VersionTwoForm = ({
  formGuid,
  portalId,
  env,
  region,
  onFormLoaded,
  onFormSubmitted
}) => {
  useEffect(() => {
    const formsDomain = env === 'qa' ? 'hsformsqa.net' : 'hsforms.net';
    const script = document.createElement('script');
    script.src = `//js.${formsDomain}/forms/embed/${portalId}.js`;
    script.type = 'text/javascript';
    document.body.appendChild(script);
    const handleFormLoaded = e => {
      if (typeof e === 'object' && e !== null && 'detail' in e) {
        const {
          formId
        } = e.detail;
        if (formId === formGuid) {
          onFormLoaded();
        }
      }
    };
    const handleFormSubmitted = e => {
      if (typeof e === 'object' && e !== null && 'detail' in e) {
        const {
          formId
        } = e.detail;
        if (formId === formGuid) {
          onFormSubmitted();
        }
      }
    };
    window.addEventListener('hs-form-event:on-ready', handleFormLoaded);
    window.addEventListener('hs-form-event:on-submission:success', handleFormSubmitted);
    return () => {
      document.body.removeChild(script);
      window.removeEventListener('hs-form-event:on-submission:success', handleFormSubmitted);
      window.removeEventListener('hs-form-event:on-ready', handleFormLoaded);
    };
  }, [portalId, onFormSubmitted, onFormLoaded, formGuid, env]);
  return /*#__PURE__*/_jsx("div", {
    className: "hs-form-frame",
    "data-env": env,
    "data-region": region,
    "data-form-id": formGuid,
    "data-portal-id": portalId
  });
};
const Wrapper = styled.div.withConfig({
  displayName: "TicketCreation__Wrapper"
})(["transition:opacity 0.2s ease-in-out;opacity:", ";.hs-email:has([name='email']){display:none;}"], ({
  $visible
}) => $visible ? 1 : 0);
export function TicketCreation() {
  const loginVerify = useLoginVerify();
  const {
    close
  } = useTicketCreation();
  const [formHasLoaded, setFormHasLoaded] = useState(false);
  const formData = useFormData();
  const portalData = usePortalData();
  const {
    portalId,
    env,
    region
  } = portalData.data;
  const {
    email
  } = loginVerify.data || {};
  useEffect(() => {
    if (email) {
      const params = new URLSearchParams(window.location.search);
      params.set('email', email);
      window.history.replaceState(null, '', `?${params.toString()}`);
      return () => {
        params.delete('email');
        window.history.replaceState(null, '', `?${params.toString()}`);
      };
    }
  }, [email]);
  const onFormLoaded = useCallback(() => {
    setFormHasLoaded(true);
  }, []);
  const onFormSubmitted = useCallback(() => {
    FloatingAlertStore.addAlert({
      message: I18n.text('customer-portal-app.ticketSubmittedAlert.message'),
      titleText: I18n.text('customer-portal-app.ticketSubmittedAlert.title'),
      type: 'success'
    });
    close();
  }, [close]);
  if (!formData.data) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      marginBlock: "400",
      "aria-label": I18n.text('customer-portal-app.loading')
    });
  }
  if (!formData.data.formCreatedByEditorV2) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", {
        children: !formHasLoaded && /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          marginBlock: "400",
          "aria-label": I18n.text('customer-portal-app.loading')
        })
      }), /*#__PURE__*/_jsx(Wrapper, {
        $visible: formHasLoaded,
        children: /*#__PURE__*/_jsx(VersionOneForm, {
          onFormLoaded: onFormLoaded,
          onFormSubmitted: onFormSubmitted,
          formGuid: formData.data.formGuid,
          portalId: portalId,
          env: env,
          region: region
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      children: !formHasLoaded && /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        marginBlock: "400",
        "aria-label": I18n.text('customer-portal-app.loading')
      })
    }), /*#__PURE__*/_jsx(Wrapper, {
      $visible: formHasLoaded,
      children: /*#__PURE__*/_jsx(VersionTwoForm, {
        onFormLoaded: onFormLoaded,
        onFormSubmitted: onFormSubmitted,
        formGuid: formData.data.formGuid,
        portalId: portalId,
        env: env,
        region: region
      })
    })]
  });
}